import React from 'react';
import ContentPageTemplate from '@/components/ContentPageTemplate';
import { Button } from '@/components/ui/button';
import SchemaMarkup from '@/components/SchemaMarkup';
import { buildFAQSchema } from '@/lib/schema';

const FAQ_ITEMS = [
  {
    question: 'Do I need a permit for impact windows in Boca Raton?',
    answer: 'Yes. All impact window installations in Palm Beach County require a building permit. We handle the entire permit process, including application, submission, and inspection coordination.',
  },
  {
    question: 'How long does installation take in Boca Raton?',
    answer: 'The entire process from contract to completion takes 8–12 weeks. Physical installation typically takes 2–4 days for luxury homes. Most of the timeline is permit approval (5–10 days) and product manufacturing (6–10 weeks for premium tiers).',
  },
  {
    question: 'Can I replace windows in a luxury condo in Boca Raton?',
    answer: 'Yes. Luxury condo buildings often have strict architectural guidelines and specific product requirements. We handle all board coordination, product submittals, and compliance documentation. Expect to add 2–4 weeks for HOA approval.',
  },
  {
    question: 'Will impact windows lower my insurance in Boca Raton?',
    answer: "Yes. Most homeowners see 15–45% premium reductions, with higher savings for waterfront and Intracoastal properties. Given Boca's higher property values, annual savings can be substantial ($2,000–$5,000+ per year).",
  },
  {
    question: 'What affects the price of impact windows in Boca Raton?',
    answer: 'Price depends on: (1) Product tier (Good/Better/Premium), (2) Property size and window count, (3) Architectural requirements (custom sizing, finishes), (4) Waterfront or Intracoastal location, and (5) HOA or community design standards.',
  },
];

export default function ImpactWindowsBocaRaton() {
  const pageData = {
    title: "Impact Windows in Boca Raton: Pricing, Permits, and Installation",
    metaDescription: "Impact windows in Boca Raton: real pricing, permit requirements, and installation timelines. Get a photo-based quote — no in-home appointment.",
    canonicalPath: "/impact-windows-boca-raton",
    
    directAnswer: (
      <>
        <p className="text-lg text-[var(--brand-slate-700)] mb-4 leading-relaxed">
          Most companies require an in-home visit just to give you pricing. We don't. Upload photos and receive clear pricing with Good, Better, and Premium options.
        </p>
        <p>
          Impact window installation in Boca Raton and Palm Beach County requires <strong>Palm Beach County Product Approval</strong>, building permits, and High Velocity Hurricane Zone (HVHZ) compliance for coastal properties. Most residential projects cost <strong>$18,000–$50,000</strong> depending on property size and product tier. Installation timelines average <strong>8–12 weeks</strong> from contract to completion.
        </p>
      </>
    ),

    sections: [
      {
        title: "Who This Is For",
        testId: "who-this-is-for",
        bullets: [
          "Homeowners comparing impact window options and pricing",
          "Luxury and waterfront property owners needing premium protection",
          "People who want pricing without a sales appointment",
          "Anyone looking for clear, transparent Good/Better/Premium options"
        ]
      },

      {
        title: "How Snap Windows Is Different",
        testId: "how-different",
        bullets: [
          "No in-home appointment required — upload photos and receive pricing online",
          "Good / Better / Premium comparison — choose what fits your budget",
          "Transparent pricing — no games, no pressure",
          "Full project management — we handle permits and inspections"
        ],
        highlight: "See how real Boca Raton homeowners chose their options on our case studies page."
      },
      {
        title: "Why Boca Raton Homeowners Choose Impact Windows",
        testId: "why-boca",
        content: (
          <p className="mb-4">
            Boca Raton's luxury properties and Intracoastal location make impact windows essential. From Royal Palm Yacht & Country Club to Highland Beach estates, impact windows are now standard for premium properties throughout Palm Beach County:
          </p>
        ),
        bullets: [
          "Hurricane Protection: Coastal and Intracoastal properties require HVHZ-rated impact windows for full protection",
          "Luxury Home Protection: Safeguard high-value properties and interior finishes during storms — critical for estates in communities like The Sanctuary and Mizner Country Club",
          "Insurance Savings: Reduce premiums by 15–45% annually with wind mitigation, with higher savings for waterfront and Intracoastal properties",
          "Noise Reduction: Block traffic from I-95, Federal Highway, A1A, and coastal boat traffic",
          "Property Value: Impact windows are expected in Boca's competitive luxury market — homes without them often face buyer objections",
          "Coastal Requirements: Properties east of US-1 typically have stricter building requirements and product specifications"
        ],
        highlight: "Palm Beach County requires Product Approval for all impact windows. We only use certified, code-compliant products. Learn more about the installation process."
      },

      {
        title: "Palm Beach County Permit Requirements",
        testId: "permits",
        content: (
          <p className="mb-4">
            Impact window installations in Palm Beach County require building permits:
          </p>
        ),
        bullets: [
          "Permit Application: Includes product approvals, installation plans, and contractor license",
          "Approval Timeline: 5–10 business days for standard residential projects",
          "Permit Fees: Typically $500–$900 depending on scope",
          "Inspection Required: County inspector verifies code compliance"
        ],
        highlight: "We handle all permit coordination. No need to visit the building department."
      },

      {
        title: "Pricing for Boca Raton Homes",
        testId: "pricing",
        table: {
          headers: ["Property Type", "Typical Openings", "Estimated Cost Range"],
          rows: [
            ["Condo/Townhome", "8–12 windows", "$18,000 – $25,000"],
            ["Single-Family Home", "15–20 windows + 2–3 doors", "$25,000 – $40,000"],
            ["Luxury Home", "20–30 windows + 3–5 doors", "$40,000 – $70,000"],
            ["Waterfront Estate", "30+ windows + 5+ doors", "$70,000 – $150,000+"]
          ]
        },
        content: (
          <p className="mt-4">
            Pricing includes product, installation, permits, old window removal, and inspection.
          </p>
        ),
        highlight: "We provide Good, Better, and Premium tiers to match your property and budget. See real pricing examples in our case studies."
      },

      {
        title: "Areas We Serve in Palm Beach County",
        testId: "areas-served",
        content: (
          <div className="mb-4">
            <p className="mb-4">We serve Palm Beach County, including:</p>
            <div className="grid md:grid-cols-3 gap-3">
              <ul className="list-disc pl-6 space-y-1 text-sm text-[var(--brand-slate-700)]">
                <li>Boca Raton</li>
                <li>Delray Beach</li>
                <li>West Palm Beach</li>
                <li>Wellington</li>
                <li>Boynton Beach</li>
              </ul>
              <ul className="list-disc pl-6 space-y-1 text-sm text-[var(--brand-slate-700)]">
                <li>Jupiter</li>
                <li>Lake Worth</li>
                <li>Palm Beach Gardens</li>
                <li>Royal Palm Beach</li>
                <li>Greenacres</li>
              </ul>
              <ul className="list-disc pl-6 space-y-1 text-sm text-[var(--brand-slate-700)]">
                <li>Palm Beach</li>
                <li>Juno Beach</li>
                <li>North Palm Beach</li>
                <li>Highland Beach</li>
                <li>Gulf Stream</li>
              </ul>
            </div>
          </div>
        )
      },

      {
        title: "Frequently Asked Questions",
        testId: "faq",
        content: (
          <div className="space-y-6">
            {FAQ_ITEMS.map(({ question, answer }, i) => (
              <div key={i}>
                <p className="font-semibold text-[var(--brand-slate-900)] mb-2">{question}</p>
                <p className="text-[var(--brand-slate-700)]">{answer}</p>
              </div>
            ))}
          </div>
        )
      }
    ],

    relatedLinks: [
      {
        title: "Get Your Quote",
        description: "Upload photos and receive pricing",
        url: "/get-quote?src=boca_raton"
      },
      {
        title: "See Real Projects",
        description: "Boca Raton case studies",
        url: "/case-studies"
      },
      {
        title: "Installation Process",
        description: "Step-by-step timeline",
        url: "/installation-process"
      },
      {
        title: "Insurance Savings",
        description: "Wind mitigation explained",
        url: "/insurance-savings"
      }
    ],

    ctaText: "Get My Price",
    ctaSubtext: "Upload photos and receive Good, Better, and Premium pricing options for your Boca Raton home. No in-home appointment required. No pressure. Just clear pricing.",

    breadcrumbs: [
      { label: "Home", link: "/" },
      { label: "Impact Windows Boca Raton" }
    ]
  };

  return (
    <>
      <SchemaMarkup schemas={buildFAQSchema(FAQ_ITEMS)} instanceId="impact-windows-boca-faq" />
      <ContentPageTemplate {...pageData} />
    </>
  );
}
